import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { artistMock } from "../mock";
import { toast } from "../hooks/use-toast";
import {
  getOwner,
  getToken,
  setToken,
  getStoredUser,
  setStoredUser,
  apiGetPortal,
  apiSavePortal,
  apiGetCollection,
  apiGetSubscription,
  apiGetExclusive,
  apiGetTickets,
  apiSupportChat,
  apiLogin,
  apiSignup,
  apiMe,
  apiUpdateProfile,
  apiStripeCheckout,
  apiStripeStatus,
  apiPaypalCreate,
  apiPaypalCapture,
  apiSavePortalSettings,
  apiRegistrationStatus,
} from "../api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const fallbackPortal = { portalType: "solo", shareCode: "", membershipPrice: 4.99, maxArtists: 10, artists: [artistMock] };

export function AppProvider({ children }) {
  const [portal, setPortal] = useState(fallbackPortal);
  const [activeArtistId, setActiveArtistId] = useState(artistMock.id);
  const [collection, setCollection] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [freeAccess, setFreeAccess] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionDetail, setSubscriptionDetail] = useState({ days_left: 0, subscription_end: null });
  const [membershipPrice, setMembershipPrice] = useState(4.99);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(null);

  // auth
  const [user, setUser] = useState(getStoredUser());
  const [isFanJoin, setIsFanJoin] = useState(false);
  const [registration, setRegistration] = useState({ active: false, free_access: false, fee: 11.0, days_left: 0, paid_through: null });
  const [authOpen, setAuthOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [exclusive, setExclusive] = useState(null);
  const [exclusiveLocked, setExclusiveLocked] = useState(false);
  const [ticketQR, setTicketQR] = useState(null); // ticket object to show QR

  // payments
  const [payItem, setPayItem] = useState(null); // {type:'song'|'membership', song?}
  const [payProcessing, setPayProcessing] = useState(false);
  const [payBanner, setPayBanner] = useState(null); // {state:'processing'|'success'|'error'|'cancel', msg}

  // UI
  const [editMode, setEditMode] = useState(false);
  const [showCollection, setShowCollection] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);

  // player
  const [current, setCurrent] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playerMinimized, setPlayerMinimized] = useState(false);
  const [playerClosed, setPlayerClosed] = useState(false);
  const [playerPos, setPlayerPos] = useState(() => {
    try { const v = localStorage.getItem("s71_player_pos"); return v ? JSON.parse(v) : null; } catch { return null; }
  });
  const audioRef = useRef(null);

  const refreshCollection = async () => {
    try {
      const d = await apiGetCollection(getOwner());
      setCollection(d.songs || []);
      setFreeAccess(!!d.free_access);
    } catch (e) { console.warn("refreshCollection failed", e); }
  };
  const refreshSubscription = async () => {
    try {
      const d = await apiGetSubscription(getOwner());
      setSubscribed(!!d.active);
      if (d.price) setMembershipPrice(d.price);
      setSubscriptionDetail({ days_left: d.days_left || 0, subscription_end: d.subscription_end || null });
    } catch (e) { console.warn("refreshSubscription failed", e); }
  };
  const refreshTickets = async () => {
    try {
      const d = await apiGetTickets(getOwner());
      setTickets(d.tickets || []);
    } catch (e) { console.warn("refreshTickets failed", e); }
  };
  const refreshExclusive = async () => {
    try {
      const d = await apiGetExclusive();
      setExclusive(d);
      setExclusiveLocked(false);
    } catch (e) {
      setExclusive(null);
      setExclusiveLocked(e?.response?.status === 403);
    }
  };

  const applyPortal = (p) => {
    if (!p || !p.artists || p.artists.length === 0) return;
    setPortal(p);
    setMembershipPrice(p.membershipPrice || 4.99);
    setActiveArtistId((prev) => (p.artists.some((a) => a.id === prev) ? prev : p.artists[0].id));
  };

  // initial load + payment return handling
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [p, c, s] = await Promise.all([
          apiGetPortal(),
          apiGetCollection(getOwner()),
          apiGetSubscription(getOwner()),
        ]);
        if (!active) return;
        applyPortal(p);
        setCollection(c.songs || []);
        setFreeAccess(!!c.free_access);
        setSubscribed(!!s.active);
        setApiError(null);
        try {
          const join = new URLSearchParams(window.location.search).get("join");
          if (join) {
            setIsFanJoin(true);
            setWelcomeOpen(true);
            const url = new URL(window.location.href);
            url.searchParams.delete("join");
            window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
          }
        } catch (e) { console.warn("join param handling failed", e); }
      } catch {
        if (active) setApiError("Could not reach the server. Showing sample data.");
      } finally {
        if (active) setLoading(false);
      }
      if (getToken()) {
        try {
          const m = await apiMe();
          if (active) {
            setUser(m.user);
            setStoredUser(m.user);
            await refreshTickets();
            await refreshExclusive();
            await refreshRegistration();
          }
        } catch {
          // token invalid
          setToken(null);
          setStoredUser(null);
          if (active) setUser(null);
        }
      }
      handlePaymentReturn();
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, []);

  // Fans can never be in edit mode
  useEffect(() => {
    if (user?.role === "fan" && editMode) setEditMode(false);
    // eslint-disable-next-line
  }, [user, editMode]);

  // Persist player position
  useEffect(() => {
    try {
      if (playerPos) localStorage.setItem("s71_player_pos", JSON.stringify(playerPos));
      else localStorage.removeItem("s71_player_pos");
    } catch (e) { console.warn("persist player position failed", e); }
  }, [playerPos]);

  const cleanUrl = () => {
    const url = new URL(window.location.href);
    ["payment", "session_id", "token", "PayerID", "ba_token"].forEach((k) => url.searchParams.delete(k));
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
  };

  const handlePaymentReturn = async () => {
    const params = new URLSearchParams(window.location.search);
    const pay = params.get("payment");
    if (!pay) return;
    if (pay === "cancel") {
      setPayBanner({ state: "cancel", msg: "Payment cancelled." });
      cleanUrl();
      setTimeout(() => setPayBanner(null), 4000);
      return;
    }
    if (pay === "stripe") {
      const sid = params.get("session_id");
      if (!sid) return;
      setPayBanner({ state: "processing", msg: "Confirming your payment..." });
      cleanUrl();
      await pollStripe(sid, 0);
    } else if (pay === "paypal") {
      const orderId = params.get("token");
      if (!orderId) {
        cleanUrl();
        return;
      }
      setPayBanner({ state: "processing", msg: "Confirming your PayPal payment..." });
      cleanUrl();
      try {
        const res = await apiPaypalCapture(orderId);
        if (res.status === "COMPLETED" && res.granted) {
          await afterGrant(res.item_type);
        } else {
          setPayBanner({ state: "error", msg: "Payment not completed. Please try again." });
          setTimeout(() => setPayBanner(null), 5000);
        }
      } catch {
        setPayBanner({ state: "error", msg: "Could not confirm PayPal payment." });
        setTimeout(() => setPayBanner(null), 5000);
      }
    }
  };

  const pollStripe = async (sid, attempt) => {
    if (attempt >= 8) {
      setPayBanner({ state: "error", msg: "Payment is taking longer than expected. Check your collection shortly." });
      setTimeout(() => setPayBanner(null), 6000);
      return;
    }
    try {
      const res = await apiStripeStatus(sid);
      if (res.payment_status === "paid" && res.granted) {
        await afterGrant(res.item_type);
        return;
      }
      if (res.status === "expired") {
        setPayBanner({ state: "error", msg: "Payment session expired. Please try again." });
        setTimeout(() => setPayBanner(null), 5000);
        return;
      }
      setTimeout(() => pollStripe(sid, attempt + 1), 1800);
    } catch {
      setTimeout(() => pollStripe(sid, attempt + 1), 1800);
    }
  };

  const refreshRegistration = async () => {
    if (!getToken()) { setRegistration({ active: false, free_access: false, fee: 11.0, days_left: 0, paid_through: null }); return; }
    try {
      const d = await apiRegistrationStatus();
      setRegistration(d);
    } catch (e) { console.warn("refreshRegistration failed", e); }
  };

  const afterGrant = async (itemType) => {
    await Promise.all([refreshCollection(), refreshSubscription(), refreshTickets(), refreshExclusive()]);
    if (itemType === "registration") {
      await refreshRegistration();
      setPayBanner({ state: "success", msg: "Portal activated — your $11/mo registration is current. You can now edit & publish." });
    } else if (itemType === "membership") {
      setPayBanner({ state: "success", msg: "Membership active — exclusive content unlocked!" });
    } else if (itemType === "ticket") {
      setPayBanner({ state: "success", msg: "Ticket purchased — find your QR in My Collection → Tickets." });
      try {
        const d = await apiGetTickets(getOwner());
        if (d.tickets && d.tickets.length) setTicketQR(d.tickets[0]);
      } catch (e) { console.warn("fetch ticket QR failed", e); }
    } else {
      setPayBanner({ state: "success", msg: "Purchase complete — added to your collection." });
    }
    setTimeout(() => setPayBanner(null), 6000);
  };

  // ---- Audio ----
  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;
    const onTime = () => setProgress(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onEnd = () => setPlaying(false);
    const onError = () => {
      setPlaying(false);
      toast({
        title: "Can't play this track",
        description: "The audio link isn't a direct, playable file (e.g. Suno share pages won't play). Upload an MP3 or paste a direct audio URL in Edit.",
        variant: "destructive",
      });
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onError);
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onError);
    };
  }, []);

  const safePlay = (audio) => {
    const p = audio.play();
    if (p && typeof p.catch === "function") p.then(() => setPlaying(true)).catch(() => setPlaying(false));
    else setPlaying(true);
  };

  const playSong = (song) => {
    const audio = audioRef.current;
    if (!audio || !song) return;
    if (current && current.id === song.id) {
      setPlayerClosed(false);
      setPlayerMinimized(false);
      if (audio.paused) {
        safePlay(audio);
      } else {
        audio.pause();
        setPlaying(false);
      }
      return;
    }
    if (!song.audioUrl) {
      toast({ title: "No audio for this track", description: "Add an audio file in Edit to make it playable.", variant: "destructive" });
      return;
    }
    setCurrent(song);
    setPlayerMinimized(false);
    setPlayerClosed(false);
    audio.src = song.audioUrl;
    safePlay(audio);
  };
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !current) return;
    if (audio.paused) {
      safePlay(audio);
    } else {
      audio.pause();
      setPlaying(false);
    }
  };
  const seek = (t) => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = t;
      setProgress(t);
    }
  };

  const ownsSong = (id) => freeAccess || collection.some((s) => s.id === id);

  // derived active artist
  const artist = (portal.artists || []).find((a) => a.id === activeArtistId) || (portal.artists || [])[0] || artistMock;

  // ---- Payment modal triggers ----
  const requestBuy = (song) => {
    if (ownsSong(song.id)) {
      toast({ title: "Already owned", description: `You already have “${song.title}”.` });
      return;
    }
    setPayItem({ type: "song", song });
  };
  const requestMembership = () => {
    if (subscribed) {
      toast({ title: "You're already a member", description: "Enjoy your exclusive access." });
      return;
    }
    setPayItem({ type: "membership", price: membershipPrice });
  };
  const requestTicket = (event) => {
    if (event.ticketUrl) {
      window.open(event.ticketUrl, "_blank", "noopener");
      return;
    }
    if (event.ticketPrice != null) {
      setPayItem({ type: "ticket", event, price: event.ticketPrice });
    }
  };
  const requestRegistration = () => {
    setPayItem({ type: "registration", price: registration.fee || 11.0 });
  };
  const closePayModal = () => setPayItem(null);

  const startPayment = async (provider) => {
    if (!payItem) return;
    setPayProcessing(true);
    let item_id = null;
    if (payItem.type === "song") item_id = payItem.song.id;
    else if (payItem.type === "ticket") item_id = payItem.event.id;
    // Registration is billed to the platform against the logged-in artist's id.
    const owner = payItem.type === "registration" ? (user?.id || getOwner()) : getOwner();
    const body = { origin_url: window.location.origin, item_type: payItem.type, item_id, owner };
    try {
      if (provider === "stripe") {
        const res = await apiStripeCheckout(body);
        window.location.href = res.url;
      } else {
        const res = await apiPaypalCreate(body);
        if (res.approve_url) window.location.href = res.approve_url;
        else throw new Error("no approve url");
      }
    } catch (e) {
      setPayProcessing(false);
      const detail = e?.response?.data?.detail;
      toast({ title: "Payment couldn't start", description: detail || "Please try again.", variant: "destructive" });
    }
  };

  const savePayoutSettings = async (payload) => {
    try {
      const saved = await apiSavePortalSettings(payload);
      applyPortal(saved);
      toast({ title: "Settings saved", description: "Your payout & fan pricing are updated." });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Could not save settings." };
    }
  };

  const sendSupport = async (message) => {
    try {
      const res = await apiSupportChat(message);
      return { ok: true, reply: res.reply };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Support is unavailable right now." };
    }
  };

  // ---- Auth ----
  const login = async (email, password) => {
    try {
      const res = await apiLogin({ email, password });
      setToken(res.token);
      setStoredUser(res.user);
      setUser(res.user);
      await Promise.all([refreshCollection(), refreshSubscription(), refreshTickets(), refreshExclusive()]);
      await refreshRegistration();
      toast({ title: `Welcome back, ${res.user.name}`, description: res.user.is_free_access ? "Full access unlocked." : "You're signed in." });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Login failed." };
    }
  };
  const signup = async (name, email, password, role = "artist") => {
    try {
      const res = await apiSignup({ name, email, password, role });
      setToken(res.token);
      setStoredUser(res.user);
      setUser(res.user);
      await Promise.all([refreshCollection(), refreshSubscription(), refreshTickets(), refreshExclusive()]);
      toast({ title: "Account created", description: role === "fan" ? "Welcome — enjoy the exclusive content." : "Welcome to S71." });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Sign up failed." };
    }
  };
  const logout = async () => {
    setToken(null);
    setStoredUser(null);
    setUser(null);
    setExclusive(null);
    setExclusiveLocked(false);
    setTickets([]);
    await Promise.all([refreshCollection(), refreshSubscription()]);
    toast({ title: "Signed out" });
  };

  const savePortal = async (next) => {
    try {
      const saved = await apiSavePortal(next);
      applyPortal(saved);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Could not save changes." };
    }
  };

  const updateProfile = async (payload) => {
    try {
      const res = await apiUpdateProfile(payload);
      if (res.token) setToken(res.token);
      setStoredUser(res.user);
      setUser(res.user);
      toast({ title: "Account updated", description: "Your details have been saved." });
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || "Could not update account." };
    }
  };

  const value = useMemo(
    () => ({
      portal, artist, activeArtistId, setActiveArtistId, savePortal, membershipPrice,
      collection, tickets, freeAccess, subscribed, subscriptionDetail, loading, apiError,
      user, authOpen, setAuthOpen, accountOpen, setAccountOpen, shareOpen, setShareOpen,
      isFanJoin, isFan: user?.role === "fan",
      canEdit: !!user && user?.role !== "fan" && (registration.active || !!user?.is_free_access),
      registration, requestRegistration, savePayoutSettings,
      needsRegistration: !!user && user?.role !== "fan" && !registration.active && !user?.is_free_access,
      welcomeOpen, setWelcomeOpen, supportOpen, setSupportOpen, login, signup, logout, updateProfile,
      exclusive, exclusiveLocked, requestTicket, ticketQR, setTicketQR, sendSupport,
      payItem, payProcessing, payBanner, setPayBanner, requestBuy, requestMembership, closePayModal, startPayment,
      editMode, setEditMode, showCollection, setShowCollection, activeVideo, setActiveVideo,
      current, playing, progress, duration, playSong, togglePlay, seek, ownsSong,
      playerMinimized, setPlayerMinimized, playerClosed, setPlayerClosed, playerPos, setPlayerPos,
    }),
    [portal, activeArtistId, membershipPrice, collection, tickets, freeAccess, subscribed, subscriptionDetail, loading, apiError,
     user, isFanJoin, registration, authOpen, accountOpen, shareOpen, welcomeOpen, supportOpen, exclusive, exclusiveLocked, ticketQR,
     payItem, payProcessing, payBanner, editMode, showCollection, activeVideo, current, playing, progress, duration,
     playerMinimized, playerClosed, playerPos]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
